import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Row, Col, Select, DatePicker, Input, Upload, Spin, Modal  } from "antd";
import { PlusOutlined, CommentOutlined, LoadingOutlined, CalendarOutlined} from '@ant-design/icons';
import customParseFormat from "dayjs/plugin/customParseFormat";
import dayjs from "dayjs";


import S3Get from '../S3Get';
import DatePickerMobileLog from './DatePickerMobileLog';

dayjs.extend(customParseFormat);

const {Option} = Select;

const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    }
);

const uploadButton = (
    <button
      style={{
        border: 0,
        background: 'none',
      }}
      type="button"
    >
      <PlusOutlined />
      <div
        style={{
          marginTop: 8,
        }}
      >
        Add
      </div>
    </button>
);

export const ActionLogData = forwardRef(({
    actionData, 
    isRep, 
    assignedView, 
    viewOnly, 
    StyledDiv, 
    logValidationErrors,
    setLogValidationErrors,
    selectedAction,
    allEmployees,
    hazardMode
}, ref) => {
    const [actionDetails, setActionDetails] = useState({comments: "", files:[], localFiles: [], defaultDueDate: null});
    const [loading, setLoading] = useState(true);
    const [validationErrors, setValidationErrors] = useState({upload: ""});
    const [maxChar, setMaxChar] = useState("0");
    const [showDatePicker, setShowDatePicker] = useState(false);

    useImperativeHandle(ref, () => ({
        getActionDetails: () => actionDetails
    }));

    useEffect(() => {
        let mounted = true; 

        const populateActionDetails = async () => {
            if(actionData){
                setActionDetails({...actionData, comments: "", files:[], localFiles: [], defaultDueDate: null});
                setLoading(false);
            }
        }

        if (mounted) {
            populateActionDetails();
        }

        return () => mounted = false;
    }, [selectedAction, actionData])

    /* Upload functions and state */
    const [uploadError, setUploadError] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');

    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
        setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
    };

    const customRequest = ({file, onSuccess}) => {  
        if(!uploadError) {
            const currentFiles = [...actionDetails.files]
            currentFiles.push(file);
            setFiles(currentFiles);
            const dummyResponse = { status: 'success', url: 'http://example.com/' + file.name };
            onSuccess(dummyResponse, file);
        }
        
    }

    const setFiles = newFiles => {
        setActionDetails(prevDetails => ({...prevDetails, files: newFiles}));
    };

    const handleChange = (info) => {
        if(!uploadError) {
            const {fileList, file} = info;
            setActionDetails(prevDetails => ({ ...prevDetails, localFiles: fileList }));
        }

    };

    const handleRemove = (file) => {
        const currentFiles = [...actionDetails.files];
        const newFiles = currentFiles.filter(f => f.uid !== file.uid);
        setFiles(newFiles);
    };

    function beforeUpload(file) {
        const allowedTypes = [
            'image/jpg',
            'image/jpeg',
            'image/png',
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-powerpoint',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        ];
    
        if (allowedTypes.includes(file.type)) {
            setUploadError(false);
            setValidationErrors( prevState => ({...prevState, upload: ""}) );
            return true;
        } else {
            setUploadError(true);
            setValidationErrors( prevState => ({...prevState, upload: "Invalid File Type: " + file.type + ". Only JPEG, PNG, PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX allowed"}) );
            return false;
        }
    }

    const handleCancel = () => setPreviewOpen(false);

    function formatDate(dateString) {
        const date = new Date(dateString);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    }


    return (
        <>
            <DatePickerMobileLog
                showDatePicker={showDatePicker} 
                setShowDatePicker={setShowDatePicker}
                StyledDiv={StyledDiv}
                actionDetails={actionDetails}
                setActionDetails={setActionDetails}
                setLogValidationErrors={setLogValidationErrors}
            />
            <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={handleCancel}>
                <img
                alt="example"
                style={{
                    width: '100%',
                }}
                    src={previewImage}
                />
            </Modal>

            <Col
                xl={{span: 16}}
                lg={{span: 24}}
                md={{span: 24}}
                sm={{span: 24}}
                xs={{span: 24}} 
                className="actionLogInputContainer"
            >
                {!loading && <>
                    {!hazardMode && <Row className='InputContainer'
                        style={{
                            marginBottom: "40px"
                        }}
                    >
                        <label><StyledDiv className="alInputTextCategory">{actionDetails.CategoryName}</StyledDiv></label>
                    </Row>}

                    {((isRep || assignedView) && !viewOnly) && <>
                        <Row className='InputContainer'>
                            {isRep && <Col
                                xl={{span: 12}}
                                lg={{span: 12}}
                                md={{span: 12}}
                                sm={{span: 24}}
                                xs={{span: 24}}
                                style={{
                                    paddingRight: "10px",
                                    marginBottom: "15px"
                                }}
                            >
                                <label><StyledDiv className="alInputTextLabel"><span style={{color: "red"}}>*</span> Status</StyledDiv></label>
                                <Select placeholder="Select Status" style={{width: "100%", marginTop: "8px", height: "40px"}} 
                                    onChange={(value) => setActionDetails(prevDetails => ({...prevDetails, status: value}))}
                                    defaultValue={actionDetails.status}
                                    value={actionDetails.status}
                                >
                                    <Option value="open">Open</Option>
                                    {(actionDetails.assignedTo !== null && actionDetails.assignedTo !== "") && <Option value="reassigned">Reassiged</Option>}
                                    <Option value="assigned">Assigned</Option>
                                    <Option value="completed">Completed</Option>
                                    <Option value="closed">Closed</Option>
                                    <Option value="void">Void</Option>
                                </Select>
                            </Col>}
                            
                            {isRep && <Col
                                xl={{span: 12}}
                                lg={{span: 12}}
                                md={{span: 12}}
                                sm={{span: 24}}
                                xs={{span: 24}}
                                style={{
                                    paddingRight: "10px",
                                    marginBottom: "15px"
                                }}
                            >
                                <label><StyledDiv className="alInputTextLabel"><span style={{color: "red"}}>*</span> Assign To</StyledDiv></label>
                                <div className="homeInfoContainer">
                                    <Select placeholder="Select Employee" style={{width: "100%", marginTop: "8px", height: "40px"}}
                                        onChange={(value) => {
                                            const emp = allEmployees.find(e => `${e.FirstName} ${e.LastName}` === value);
                                            setActionDetails(prevDetails => ({
                                                ...prevDetails,
                                                assignedTo: value,
                                                assignedToEmail: emp?.Email,
                                                businessUnit: emp?.OrgUnit,
                                            }));
                                            setLogValidationErrors(prevState => ({...prevState, assignedTo: ""}));
                                        }}
                                        showSearch
                                        optionFilterProp="children"
                                        defaultValue={actionDetails.assignedTo}
                                        value={actionDetails.assignedTo}
                                        className={logValidationErrors.assignedTo !== "" ? "alInputText homeValidationInputText" : "alInputText"}
                                    >
                                        {allEmployees.map((employee) => (
                                            <Option key={employee.Id} value={`${employee.FirstName} ${employee.LastName}`} className="newInputText">{employee.FullName}</Option>
                                        ))}
                                    </Select>
                                    {logValidationErrors.assignedTo !== "" &&
                                        <span className="homeValidationMessage2">{logValidationErrors.assignedTo}</span>
                                    }
                                </div>
                            </Col>}
                        </Row>

                        <Row >
                            <Col
                                xl={{span: 12}}
                                lg={{span: 12}}
                                md={{span: 12}}
                                sm={{span: 24}}
                                xs={{span: 24}}
                                style={{
                                    paddingRight: "10px",
                                    marginBottom: "15px"
                                }}
                            >
                                <label><StyledDiv className="alInputTextLabel"><span style={{color: "red"}}>*</span> Due Date (YYYY-MM-DD)</StyledDiv></label>
                                <div className="homeInfoContainer">
                                    <DatePicker style={{width: "100%", marginTop: "8px", height: "40px"}}
                                        disabled={!isRep}
                                        onChange={(date, dateString) => {
                                            setActionDetails(prevDetails => ({...prevDetails, dueDate: dateString, defaultDueDate: date}));
                                            setLogValidationErrors( prevState => ({...prevState, dueDate: ""}) );
                                        }}
                                        format="YYYY-MM-DD"
                                        className={logValidationErrors.dueDate !== "" ? "homeDatePickerDesktop alInputText homeValidationInputText" : "homeDatePickerDesktop alInputText"}
                                        placeholder='Select Due Date'
                                        value={actionDetails.dueDate === '' || actionDetails.dueDate === null ? actionDetails.defaultDueDate : dayjs(actionDetails.dueDate, 'YYYY-MM-DD')}
                                    />
                                    <input
                                        className={logValidationErrors.dueDate !== "" ? "alInputText homeValidationDTInputText" : "homeDatepickerMobileInput alInputText"}
                                        value={actionDetails.dueDate === '' || actionDetails.dueDate === null ? "" : actionDetails.dueDate}
                                        onChange={(e) => {}}
                                        onClick={() => {
                                            if(isRep) {
                                                setShowDatePicker(true)
                                            }
                                        }}
                                        disabled={!isRep}
                                    />
                                    <CalendarOutlined 
                                        onClick={() => {
                                            if(isRep) {
                                                setShowDatePicker(true)
                                            }
                                        }}
                                        className="datepickerMobileIcon"
                                    />
                                    {logValidationErrors.dueDate !== "" &&
                                        <span className="homeValidationMessageDate">{logValidationErrors.dueDate}</span>
                                    }
                                </div>
                            </Col>
                        </Row>

                        <Row >
                            <Col
                                xl={{span: 24}}
                                lg={{span: 24}}
                                md={{span: 24}}
                                sm={{span: 24}}
                                xs={{span: 24}}
                                style={{
                                    paddingRight: "10px",
                                    marginBottom: "15px"
                                }}
                            >
                                {isRep && <label><StyledDiv className="alInputTextLabel">Comments</StyledDiv></label>}
                                {(!isRep && (actionData.status !== "completed" && actionData.status !== "closed" && actionData.status !== "void")) && <label><StyledDiv className="alInputTextLabel"><span style={{color: "red"}}>*</span> Action Taken</StyledDiv></label>}
                                {(isRep || (!isRep && (actionData.status !== "completed" && actionData.status !== "closed" && actionData.status !== "void"))) && <div className="homeInfoContainer">
                                    <Input.TextArea 
                                        value={actionDetails.comments}
                                        onChange={(e) => {
                                            setActionDetails(prevDetails => ({...prevDetails, comments: e.target.value}));
                                            setMaxChar(e.target.value.length);
                                            setLogValidationErrors( prevState => ({...prevState, comments: ""}) );
                                        }}
                                        maxLength={1000}
                                        placeholder="Enter Comments"
                                        className={logValidationErrors.comments !== "" ? "alInputText validationInputText" : "alInputText"}
                                        style={{
                                            resize: "none",
                                            height: "100px",
                                            marginTop: "8px"
                                        }}
                                        
                                    />
                                    <span className="maxCharMessage">{maxChar}/1000</span>
                                    {logValidationErrors.comments !== "" &&
                                        <span className="homeValidationMessage">{logValidationErrors.comments}</span>
                                    }
                                </div>}
                            </Col>
                        </Row>

                        {(isRep || (!isRep && (actionData.status !== "completed" && actionData.status !== "closed" && actionData.status !== "void"))) && <>
                            <Row
                                style={{ display: 'flex', flexDirection: 'column' }}
                            >
                                <label><StyledDiv className="alInputTextLabel">Uploads & Attachments</StyledDiv></label>
                                {validationErrors.upload !== '' && <label><StyledDiv className="alErrorText" style={{marginTop: "5px"}}>{validationErrors.upload}</StyledDiv></label>}
                                
                            </Row>

                            <Row
                                style={{ display: 'flex', flexDirection: 'column' }}
                            >
                                <label><StyledDiv className="alInputText"
                                    style={{
                                        marginTop: "5px",
                                        fontStyle: 'italic',
                                        textDecoration: 'underline'
                                    }}
                                >Supporting evidence for the closure of actions must be uploaded to record</StyledDiv></label>
                                
                            </Row>

                            <Row
                                style={{
                                    marginTop: "20px",
                                    marginBottom: "130px"
                                }}
                            >
                                <Upload
                                    listType="picture-card"
                                    fileList={actionDetails.localFiles}
                                    onPreview={handlePreview}
                                    customRequest={customRequest}
                                    onChange={handleChange}
                                    onRemove={handleRemove}
                                    beforeUpload={beforeUpload}
                                >
                                    {actionDetails.localFiles.length >= 8 ? null : uploadButton}
                                </Upload>
                            </Row>
                        </>}
                    </>}

                    {viewOnly && <Row className='InputContainer'>
                        <Col
                            xl={{span: 12}}
                            lg={{span: 12}}
                            md={{span: 12}}
                            sm={{span: 24}}
                            xs={{span: 24}}
                            style={{
                                paddingRight: "10px",
                                marginBottom: "15px"
                            }}
                        >
                            <label><StyledDiv className="alInputTextLabel"><span style={{color: "red"}}>*</span> Status</StyledDiv></label>
                            <Input 
                                disabled={true}
                                value={actionDetails.status}
                                className="alInputText"
                            />
                        </Col>

                        <Col
                            xl={{span: 12}}
                            lg={{span: 12}}
                            md={{span: 12}}
                            sm={{span: 0}}
                            xs={{span: 0}}
                        >
                        </Col>
                    </Row>}

                    <Row
                        style={{
                            marginBottom: "20px"
                        }}
                    >
                        <label><StyledDiv className="alInputTextLabel"> Log</StyledDiv></label>
                        
                    </Row>

                    {actionData.logs.items.length > 0 && actionData.logs.items.map((log, index) => {
                        const newIndex = index
                        return (
                            <React.Fragment key={log.id + index}>
                                <Row
                                    style={{
                                        marginBottom: "10px"
                                    }}
                                >
                                    <Col
                                        xl={{span: 24}}
                                        lg={{span: 24}}
                                        md={{span: 24}}
                                        sm={{span: 24}}
                                        xs={{span: 24}}
                                    >
                                        <Row
                                            className=""
                                        >
                                            <CommentOutlined 
                                                style={{
                                                    fontSize: "20px",
                                                    color: "#007B83",
                                                    marginRight: "5px"
                                                }}
                                            />
                                            <label><StyledDiv className="alInputTextLabel"
                                                style={{
                                                    color: "#007B83",
                                                    fontSize: "13px"
                                                }}
                                            > {log.personnelName} <span style={{fontSize: "11px"}}>{formatDate(log.createdAt)}</span></StyledDiv></label>
                                            
                                        </Row>

                                        <Row
                                            style={{
                                                display: "flex",
                                                justifyContent: "start",
                                                paddingTop: "10px",
                                                paddingLeft: "20px"
                                            }}
                                        >
                                            <Col
                                                xl={{span: 24}}
                                                lg={{span: 24}}
                                                md={{span: 24}}
                                                sm={{span: 24}}
                                                xs={{span: 24}}
                                            >
                                                <StyledDiv
                                                    style={{
                                                        fontSize: "12px",
                                                        fontWeight: "500",
                                                        whiteSpace: "pre-wrap"
                                                    }}
                                                >
                                                    {log.logDetails}
                                                </StyledDiv>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>

                                <Row
                                     style={{
                                        marginBottom: "20px"
                                    }}
                                >
                                    {log.files && log.files.length > 0 && log.files.map((file, index) => {
                                        const fileName = file.split('public/')
                                        return (
                                            <Col
                                                xl={{span: 6}}
                                                lg={{span: 6}}
                                                md={{span: 12}}
                                                sm={{span: 12}}
                                                xs={{span: 12}}
                                                key={fileName + index}
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "start",
                                                    alignItems: "center",
                                                    marginBottom: "20px",
                                                    paddingLeft: "20px"
                                                }}
                                            >
                                                <S3Get
                                                    key={`${newIndex}-${index}`}
                                                    image={fileName[1]}
                                                    log={true}
                                                />
                                            </Col>
                                        )
                                    })}
                                </Row>
                            </React.Fragment>
                            
                        )
                    })}
                </>}

                {loading && <Row
                    style={{
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 70, color: "rgb(174,32,114)" }} spin />} spinning={true}></Spin>
                    </div>
                </Row>}

            </Col>
        </>
    )
})
